import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  Users, FileWarning, Building2, BriefcaseBusiness, ShieldCheck, UserCheck,
  Clock, LogOut, ArrowUpRight, AlertTriangle
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell
} from "recharts";

function StatCard({ label, value, icon: Icon, tone = "navy", to }) {
  const tones = {
    navy: "text-navy bg-navy/5",
    teal: "text-teal bg-teal/10",
    amber: "text-amber-600 bg-amber-50",
    red: "text-red-600 bg-red-50",
    emerald: "text-emerald-600 bg-emerald-50",
  };
  const inner = (
    <Card className="p-5 border-slate-200 shadow-none hover:border-teal/40 btn-lift group" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div className={`h-11 w-11 rounded-lg grid place-items-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        {to && <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-teal transition-colors" />}
      </div>
      <p className="font-serif-display text-4xl tracking-tight text-navy mt-4">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });
  const { data: comp } = useQuery({
    queryKey: ["compliance"],
    queryFn: async () => (await api.get("/dashboard/compliance")).data,
  });

  if (isLoading || !data) return <Loading label="Loading workforce overview…" />;

  const e = data.employees, d = data.documents;
  const chartData = (data.by_department || []).slice(0, 8);
  const palette = ["#042C53", "#0a8f8f", "#0A3A6B", "#0c7d7d", "#3b6ea5", "#5fb0b0"];

  return (
    <div>
      <PageHeader
        overline="Workforce Overview"
        title="HR & Competency Dashboard"
        subtitle="Live compliance and workforce status across the laboratory."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger mb-6">
        <StatCard label="Total Employees" value={e.total} icon={Users} tone="navy" to="/employees" />
        <StatCard label="Active" value={e.active} icon={UserCheck} tone="emerald" to="/employees" />
        <StatCard label="On Probation" value={e.probation} icon={Clock} tone="amber" />
        <StatCard label="Relieved / Archived" value={e.left} icon={LogOut} tone="navy" />
        <StatCard label="Expiring Documents" value={d.expiring} icon={FileWarning} tone="amber" to="/compliance" />
        <StatCard label="Expired Documents" value={d.expired} icon={AlertTriangle} tone="red" to="/compliance" />
        <StatCard label="Departments" value={data.masters.departments} icon={Building2} tone="teal" to="/departments" />
        <StatCard label="Designations" value={data.masters.designations} icon={BriefcaseBusiness} tone="teal" to="/designations" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline text-teal">Headcount</p>
              <h3 className="font-serif-display text-2xl text-navy tracking-tight">Employees by Department</h3>
            </div>
          </div>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-400 py-16 text-center">No active employees yet. Add your first employee to see distribution.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ left: -18, right: 8 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-12} textAnchor="end" height={54} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip cursor={{ fill: "rgba(10,143,143,0.06)" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={54}>
                  {chartData.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6 border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-teal" />
            <h3 className="font-serif-display text-2xl text-navy tracking-tight">Compliance Alerts</h3>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="compliance-alerts">
            {(comp?.expiring_documents || []).length === 0 ? (
              <div className="text-center py-10">
                <ShieldCheck className="h-9 w-9 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">All documents compliant.</p>
              </div>
            ) : comp.expiring_documents.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{it.document_type}</p>
                  <p className="text-xs text-slate-500 truncate">{it.employee_name} · {it.employee_code}</p>
                </div>
                <div className="text-right shrink-0">
                  <StatusBadge status={it.expiry_status} />
                  <p className="text-[11px] text-slate-400 mt-1">{it.expiry_date || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
